import { asc, eq, inArray } from "drizzle-orm";
import type { DbClient } from "@/server/platform/db/client";
import {
  productBundleLines,
  products,
  productVariants,
} from "@/server/platform/db/schema/products";
import type { DbTransaction } from "@/shared/types";
import type { BundleLineInput } from "../bundle";
import type { ProductBundleLineDTO } from "../types";

type DbLike = DbTransaction | DbClient;

export async function listBundleLinesForVariantIds(
  client: DbLike,
  bundleVariantIds: string[],
): Promise<ProductBundleLineDTO[]> {
  if (bundleVariantIds.length === 0) return [];

  const rows = await client
    .select({
      id: productBundleLines.id,
      bundleVariantId: productBundleLines.bundleVariantId,
      componentVariantId: productBundleLines.componentVariantId,
      quantity: productBundleLines.quantity,
      sortOrder: productBundleLines.sortOrder,
      componentProductId: products.id,
      componentVariantName: productVariants.name,
      componentProductName: products.name,
    })
    .from(productBundleLines)
    .innerJoin(
      productVariants,
      eq(productBundleLines.componentVariantId, productVariants.id),
    )
    .innerJoin(products, eq(productVariants.productId, products.id))
    .where(inArray(productBundleLines.bundleVariantId, bundleVariantIds))
    .orderBy(
      asc(productBundleLines.bundleVariantId),
      asc(productBundleLines.sortOrder),
      asc(productBundleLines.id),
    );

  return rows;
}

export function sanitizeBundleLines(
  lines: BundleLineInput[],
): BundleLineInput[] {
  return lines.filter(
    (line) => line.componentVariantId?.trim() && line.quantity >= 1,
  );
}

export async function replaceBundleLinesForVariant(
  client: DbTransaction,
  bundleVariantId: string,
  lines: BundleLineInput[],
) {
  const sanitized = sanitizeBundleLines(lines);

  await client
    .delete(productBundleLines)
    .where(eq(productBundleLines.bundleVariantId, bundleVariantId));

  if (sanitized.length === 0) return;

  await client.insert(productBundleLines).values(
    sanitized.map((line, index) => ({
      bundleVariantId,
      componentVariantId: line.componentVariantId.trim(),
      quantity: line.quantity,
      sortOrder: line.sortOrder ?? index,
    })),
  );
}

export async function deleteBundleLinesForVariants(
  client: DbTransaction,
  bundleVariantIds: string[],
) {
  if (bundleVariantIds.length === 0) return;
  await client
    .delete(productBundleLines)
    .where(inArray(productBundleLines.bundleVariantId, bundleVariantIds));
}
